import React, { useMemo, useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Chip,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  LocalOfferOutlined,
  PercentOutlined,
  AttachMoneyOutlined,
  EuroOutlined,
  CardGiftcardOutlined,
  LocalShippingOutlined,
  InfoOutlined,
  AccessTimeOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface CouponTemplate {
  couponTemplateNumber: string;
  name: string;
  valueType: string;
  activationType?: string;
  discountPercentage?: number;
  discountAmount?: number;
  minimumPurchaseAmount?: number;
  validTo?: string;
  validityPeriodDays?: number;
  codeType?: string;
  requiredPoints?: number;
}

interface CouponOption {
  id: string;
  name: string;
  type: string;
  value: string;
  description: string;
  template: CouponTemplate;
}

interface CouponSelectionProps {
  value: string;
  options: CouponOption[];
  isRecurring: boolean;
  onChange: (couponId: string) => void;
  error?: string;
  onCategoryChange?: (category: 'free' | 'purchasable') => void;
  disabled?: boolean;
}

const CouponSelection: React.FC<CouponSelectionProps> = ({
  value,
  options,
  isRecurring,
  onChange,
  error,
  onCategoryChange,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const isPurchasableOption = (coupon: CouponOption): boolean =>
    coupon.template.activationType === 'POINTS' || (coupon.template.requiredPoints ?? 0) > 0;

  const freeOptions = useMemo(
    () => options.filter((coupon) => !isPurchasableOption(coupon)),
    [options]
  );

  const purchasableOptions = useMemo(
    () => options.filter((coupon) => isPurchasableOption(coupon)),
    [options]
  );

  const selectedCoupon = options.find((option) => option.id === value);

  const [activeCategory, setActiveCategory] = useState<'free' | 'purchasable'>('free');

  useEffect(() => {
    if (selectedCoupon) {
      const category = isPurchasableOption(selectedCoupon) ? 'purchasable' : 'free';
      setActiveCategory(category);
      onCategoryChange?.(category);
    }
  }, [selectedCoupon, onCategoryChange]);

  const visibleOptions = activeCategory === 'free' ? freeOptions : purchasableOptions;

  const getValueTypeIcon = (valueType: string) => {
    switch (valueType) {
      case 'PERCENTAGE':
        return <PercentOutlined sx={{ fontSize: 16 }} />;
      case 'FIXED_AMOUNT':
        return <AttachMoneyOutlined sx={{ fontSize: 16 }} />;
      case 'FREE_SHIPPING':
        return <LocalShippingOutlined sx={{ fontSize: 16 }} />;
      case 'GIFT':
        return <CardGiftcardOutlined sx={{ fontSize: 16 }} />;
      default:
        return <LocalOfferOutlined sx={{ fontSize: 16 }} />;
    }
  };

  const getValueTypeColor = (valueType: string): 'primary' | 'success' | 'secondary' | 'warning' => {
    switch (valueType) {
      case 'PERCENTAGE':
        return 'primary';
      case 'FIXED_AMOUNT':
        return 'success';
      case 'FREE_SHIPPING':
        return 'secondary';
      case 'GIFT':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const formatValueType = (valueType?: string) => {
    if (!valueType) return '-';
    let formatted = valueType.replaceAll('_', ' ').toLowerCase();
    formatted = formatted.replace(/\b\w/g, l => l.toUpperCase());
    return formatted;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LocalOfferOutlined color="primary" />
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {t('campaignExperimental.create.couponConfigurationTitle')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('campaignExperimental.create.couponConfigurationDesc')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('campaignExperimental.create.couponTypeLabel')}
                </Typography>
                <FormControl component="fieldset" fullWidth disabled={disabled}>
                  <RadioGroup
                    row
                    value={activeCategory}
                    onChange={(event) => {
                      const newValue = event.target.value as 'free' | 'purchasable';
                      setActiveCategory(newValue);
                      onCategoryChange?.(newValue);
                    }}
                  >
                    <FormControlLabel
                      value="free"
                      control={<Radio />}
                      label={`${t('campaignExperimental.create.couponTypeFree')} (${freeOptions.length})`}
                      disabled={disabled || freeOptions.length === 0}
                    />
                <FormControlLabel
                  value="purchasable"
                  control={<Radio />}
                  label={
                    isRecurring
                      ? t('campaignExperimental.create.couponTypePurchasableDisabled')
                      : `${t('campaignExperimental.create.couponTypePurchasable')} (${purchasableOptions.length})`
                  }
                  disabled={disabled || isRecurring || purchasableOptions.length === 0}
                />
                  </RadioGroup>
                </FormControl>
              </Box>

              <FormControl fullWidth error={!!error} disabled={disabled}>
                <InputLabel>{t('campaignExperimental.create.selectCouponLabel')}</InputLabel>
                <Select
                  value={value}
                  label={t('campaignExperimental.create.selectCouponLabel')}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={disabled}
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {t('campaignExperimental.create.chooseCouponPlaceholder')}
                  </MenuItem>
                  {visibleOptions.map((coupon) => {
                    return (
                      <MenuItem key={coupon.id} value={coupon.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          {getValueTypeIcon(coupon.template.valueType)}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {coupon.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {coupon.description}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={coupon.value}
                              size="small"
                              color={getValueTypeColor(coupon.template.valueType)}
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
                {error && <FormHelperText>{error}</FormHelperText>}
                <Typography variant="caption" color="text.secondary">
                  {activeCategory === 'free'
                    ? t('campaignExperimental.create.availableFreeCoupons', { count: freeOptions.length })
                    : t('campaignExperimental.create.availablePurchasableCoupons', { count: purchasableOptions.length })}
                </Typography>
              </FormControl>
            </Box>
          </Grid>

          {selectedCoupon && (
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'stretch' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.200',
                  borderRadius: 2,
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <InfoOutlined color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="medium" color="primary.main">
                    {t('campaignExperimental.create.selectedCouponDetailsTitle')}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', borderRadius: 1, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocalOfferOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                          {t('campaignExperimental.create.valueTypeLabel')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="medium">
                        {formatValueType(selectedCoupon.template.valueType)}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', borderRadius: 1, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EuroOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                          {t('campaignExperimental.create.minPurchaseLabel')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedCoupon.template.minimumPurchaseAmount
                          ? `€${selectedCoupon.template.minimumPurchaseAmount}`
                          : t('campaignExperimental.create.noMinimum')}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'white', borderRadius: 1, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <AccessTimeOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                          {t('campaignExperimental.create.validityPeriodLabel')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedCoupon.template.validityPeriodDays
                          ? `${selectedCoupon.template.validityPeriodDays} ${t('campaignExperimental.create.daysLabel')}`
                          : t('campaignExperimental.create.noLimit')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CouponSelection;
